import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Card,
  CardBody,
  TabPane,
  TabContent,
  Col,
} from 'reactstrap';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import NavTab from '../../shared/components/custom/NavTab';
import Api from '../../util/api';
import Loading from '../../shared/components/custom/Loading';
import { partial } from '../../util/translation/translation';
import moment from 'moment';
import useConfig from '../../util/useConfig';
import LotteryForm from './components/LotteryForm';
import LotteryContactFormPage from './LotteryContactFormPage';
import LotteryTicketsPage from './components/LotteryTicketsPage';
import clubConfig from '../../config';

const getPermittedCountries = (currency) => {
  switch (currency) {
    case 'SEK':
      return ['SE'];
    default:
      return [];
  }
};
const LotteryFormPage = () => {
  const { lotteryID } = useParams();
  const clone = window.location.pathname.includes('clone');
  const editMode = lotteryID !== undefined && !clone;
  const config = useConfig();

  const [tab, setTab] = useState('0');
  const [lottery, setLottery] = useState(null);
  const [lotteryPrizes, setLotteryPrizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLotteryPrizes, setLoadingLotteryPrizes] = useState(true);
  const history = useHistory();

  const p = partial('lotteries');
  const s = partial('shared');
  const v = partial('validateErrors');
  const getLottery = async () => {
    setLoading(true);
    try {
      const resLottery = await Api.lottery.getLotteryById(lotteryID);

      setLottery({
        ...resLottery,
      });
      setLoading(false);
    } catch (err) {
      toast.error(JSON.stringify(err));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (lotteryID) {
      getLottery();
    }
    getLotteryPrizes();
  }, [lotteryID]);

  const getLotteryPrizes = async () => {
    setLoadingLotteryPrizes(true);
    try {
      const resLotteryPrizes = await Api.lottery.prizes.getAllPrizes();
      setLotteryPrizes(resLotteryPrizes);
      setLoadingLotteryPrizes(false);
    } catch (err) {
      toast.error(p('prizesRetrievingERROR'));
      setLoadingLotteryPrizes(false);
    }
  };
  const onLotterySubmit = async (values) => {
    if (!values.name) {
      toast.error(v('nameFieldEmpty'));
      return;
    }
    const payload = {
      name: values.name,
      description: values.description || '',
      isActive: values.isActive || false,
      image: values.headingImage?.croppedBase64 || values.headingImage?.preview,
      homeImage: values.homeImage?.croppedBase64 || values.homeImage?.preview,
      sponsorImage:
        values.sponsorImage?.croppedBase64 || values.sponsorImage?.preview,
      awayImage: values.awayImage?.croppedBase64 || values.awayImage?.preview,
      ticketPrice: Number(values.ticketPrice),
      currency: config.currencyCaps,
      startDate: moment(values.startDate),
      endDate: values.endDate ? moment(values.endDate) : null,
      sponsorLink: values.sponsorLink,
      sponsorName: values.sponsorName,
      permittedCountries: getPermittedCountries(config.currencyCaps),
      drawType: values.drawType.value,
      type: values.type.value,
      winningSplitPercent: Number(values.winningSplitPercent),
      maxPrizePerWinner: Number(values.maxPrizePerWinner),
      lotteryPermissionType: clubConfig.lotteryPermission.lotteryPermissionType,
      lotteryPermissionLatitude:
        clubConfig.lotteryPermission.lotteryPermissionLatitude,
      lotteryPermissionLongitude:
        clubConfig.lotteryPermission.lotteryPermissionLongitude,
      lotteryPermissionRadius:
        clubConfig.lotteryPermission.lotteryPermissionRadius,
    };
    setLoading(true);
    try {
      let finalLotteryId = lotteryID;
      if (lotteryID && !clone) {
        await Api.lottery.updateLottery(lotteryID, payload);
      } else {
        const resLottery = await Api.lottery.createLottery(payload);
        finalLotteryId = resLottery.id;
      }
      const prizeIds = values?.prizes?.map((prize) => prize.value) || [];
      if (prizeIds.length > 0) {
        await Api.lottery.prizes.setPrizesOnLottery(finalLotteryId, {
          prizeIds,
        });
      }
      setLoading(false);
      toast.success(p('lotterySaved'));
      if (!editMode) {
        history.replace(`/lottery`);
      }
    } catch (err) {
      console.log(err);
      if (err.data.message === 'lottery.AlreadyExists') {
        toast.error(p('lotteryAlreadyExists'));
      } else {
        toast.error(s('somethingWentWrong'));
      }
      setLoading(false);
    }
  };

  return (
    <Container>
      <Loading loading={loading || loadingLotteryPrizes} />
      <Row>
        <Col>
          <Card>
            <CardBody className="p-0">
              <NavTab
                tabs={[
                  { tab: `${s('details')}` },
                  { disabled: !editMode, tab: s('communication') },
                  { disabled: !editMode, tab: p('tickets') },
                ]}
                setTab={setTab}
                activeTab={tab}
                size="sm"
              />
              <TabContent activeTab={tab}>
                <TabPane tabId="1" className="m-3">
                  <LotteryForm
                    type={lotteryID ? 'edit' : 'create'}
                    lottery={lottery}
                    clone={clone}
                    onSubmit={onLotterySubmit}
                    lotteryPrizes={lotteryPrizes}
                  />
                </TabPane>
                {editMode && (
                  <>
                    <TabPane tabId="2" className="m-3">
                      <LotteryContactFormPage lottery={lottery} />
                    </TabPane>
                    <TabPane tabId="3">
                      <LotteryTicketsPage
                        refreshLottery={getLottery}
                        lotteryId={lotteryID}
                        drawType={lottery?.drawType}
                        drawDate={lottery?.drawDate}
                      />
                    </TabPane>
                  </>
                )}
              </TabContent>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LotteryFormPage;
